import { useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { Box, Skeleton } from "@salestrak/ui";
import { PageHeader, PageHeaderTitle, SettingsBackBar, SettingsBackBarClearance } from "#app/components/page-header";
import { SettingsPageSkeleton } from "#app/components/settings/settings-page-skeleton";
import { useApiKeys, useApiKeyMutations } from "#app/hooks/settings/use-api-key-mutations";
import { ApiKeyDetailView } from "#app/components/settings/api-key-detail-view";
import { TrashConfirmDialog } from "#app/components/settings/trash-confirm-dialog";
import { useAppNavigate } from "#app/lib/hooks/use-app-navigate";
import { withPermission } from "#app/components/permissions/require-permission";

function ApiKeyDetailPage() {
  const { apiKeyId } = Route.useParams();
  const { push, replace } = useAppNavigate();
  const { apiKeys, isLoading } = useApiKeys();
  const { revokeApiKey } = useApiKeyMutations();

  const [showRevoke, setShowRevoke] = useState(false);

  const apiKey = apiKeys.find((k) => k.apiKeyId === apiKeyId);

  if (isLoading && !apiKey) {
    return (
      <Box direction="column" fill="flex" position="relative">
        {/* Real chrome: back bar + header geometry hold through the resolve;
          * only the key name is a bar. The settled Key card is 3 rows. */}
        <SettingsBackBar label="API" onClick={() => replace("/settings/api")} />
        <Box fill="flex" overflow="auto">
          <SettingsBackBarClearance />
          <PageHeader centered pinnedBack>
            <PageHeaderTitle>
              <Skeleton delayed height={20} width={180} />
            </PageHeaderTitle>
          </PageHeader>
          <SettingsPageSkeleton groups={[{ rows: 3 }]} />
        </Box>
      </Box>
    );
  }

  if (!apiKey) {
    replace("/settings/api");
    return null;
  }

  async function handleRevoke() {
    await revokeApiKey(apiKeyId);
    replace("/settings/api");
  }

  return (
    <>
      <ApiKeyDetailView
        apiKey={apiKey}
        onBack={() => replace("/settings/api")}
        onEdit={() =>
          push(`/settings/api/keys/${apiKeyId}/edit`)
        }
        onRevoke={() => setShowRevoke(true)}
      />

      <TrashConfirmDialog
        open={showRevoke}
        onOpenChange={(open) => { if (!open) setShowRevoke(false); }}
        title="Revoke API key?"
        description={`This will permanently revoke "${apiKey.name}". Any applications using this key will lose access immediately.`}
        confirmLabel="Revoke"
        onConfirm={handleRevoke}
      />
    </>
  );
}

export const Route = createFileRoute(
  "/(app)/$workspace/settings/api/keys/$apiKeyId/",
)({ component: withPermission("workspace.integrations", ApiKeyDetailPage) });
